<template>
  <template v-if="isExtra">
    <LcSeparator class="my-200" />

    <header class="flex items-center justify-between pb-200">
      <LcLotteryRemainingPicks
        :complete="remaining <= 0"
        :done-label="$t('numberlottery.remainingnumbers.done')"
        v-slot="{ countClass }"
      >
        <EditorRemainingHeadline
          v-bind="{ countClass }"
          keypath="numberlottery.remainingnumbers"
          :name="title"
          :remaining="visualRemaining"
        />
      </LcLotteryRemainingPicks>

      <EditorNumberLotteryExtraSwitch
        v-if="isExtraHitAvailable"
        v-bind="{ config, lotteryId, play }"
      />
    </header>
  </template>

  <LcLotteryNumberSelector
    v-bind="{ selectableMax, selectableNumbers, value, numberClass }"
    :all-numbers-toggled="isExtra && isExtraHitEnabled"
    :highlighted-numbers="getRollingValues(play.id, name)"
    :label="$t('numberlottery.playslip.selector.legend')"
    variant="round"
    :style="theme"
    :id="selectorId"
    @complete="emit('complete')"
    @update:value="handleUpdate"
  />
</template>

<script lang="ts" setup>
import type { LotteryId } from '#api/shared/@types/generated/backend/graphql-schema-types'

import type { Config } from '../@types/editor'

import { NUMBER_LOTTERY_FIELD_NAME } from '../constants/numberLottery'
import { type EditorPlay, useNumberLotteryStore } from '../stores/numberLottery'
import { findFieldIndexByName } from '../utils/editorFields'

type Emits = {
  (e: 'complete'): void
}

type Props = {
  config: Config
  lotteryId: LotteryId
  name: string
  play: EditorPlay
  selectableMax: number
  selectableNumbers: number[]
}

const emit = defineEmits<Emits>()
const { config, lotteryId, name, play, selectableMax } = defineProps<Props>()

const { t } = useI18n()
const store = useNumberLotteryStore(lotteryId)
const { extraBallBg, extraBallColor } = useNumberLotteryTheme(lotteryId)
const { getDisplayPlay, getNumberState, getRollingValues, isAnimating } =
  useEditorQuickPickAnimation()

const displayPlay = computed(() => getDisplayPlay(play))

const isExtra = computed(() => name === NUMBER_LOTTERY_FIELD_NAME.Extra)

const isExtraHitAvailable = computed(() => store.isExtraHitAvailable(config))

const fieldIndex = computed(() => findFieldIndexByName(play, name))

const isExtraHitEnabled = computed(() => store.isExtraHitEnabled(play))

const remaining = computed(() =>
  fieldIndex.value < 0 ? 0 : store.remainingInField(play, fieldIndex.value, selectableMax),
)

const visualRemaining = useEditorVisualRemaining(() => remaining.value)

const selectorId = computed(() => `${play.id}-${name.toLowerCase()}`)

const theme = computed(() => {
  if (!isExtra.value || !extraBallBg.value) return undefined
  return {
    [`--lottery-number-rounded-${selectorId.value}-bg`]: extraBallBg.value,
    [`--lottery-number-rounded-${selectorId.value}-color`]: extraBallColor.value,
  }
})

const title = computed(() => {
  const suffix = isExtra.value ? `extra.${String(lotteryId)}` : 'base'
  return t(`numberlottery.remainingnumbers.name.${suffix}`, visualRemaining.value)
})

const value = computed(() =>
  fieldIndex.value < 0 ? [] : displayPlay.value.fields[fieldIndex.value].selected,
)

const numberClass = (value: number) => {
  const state = getNumberState(play.id, value, name)
  return state === 'locked' ? 'number-lottery-ball-locked' : ''
}

const handleUpdate = (numbers: number[]) => {
  if (fieldIndex.value < 0 || isAnimating(play.id)) return
  store.setSelectedNumbers(play.id, fieldIndex.value, numbers)
}
</script>
